import { NativeBiometric } from '@capgo/capacitor-native-biometric';
import { Preferences } from '@capacitor/preferences';
import { isNative } from './platform';

const BIOMETRIC_CREDENTIALS_KEY = 'valueplus_bio_credentials';
const BIOMETRIC_ENABLED_KEY = 'valueplus_bio_enabled';

/**
 * Biometric Authentication Service for ValuePlus App
 * Supports fingerprint (Android) and Face ID / Touch ID (iOS)
 */

/**
 * Check if biometric authentication is available on device
 */
export const isBiometricAvailable = async () => {
  if (!isNative()) return { available: false, biometryType: 'none' };

  try {
    const result = await NativeBiometric.isAvailable();
    return {
      available: result.isAvailable,
      biometryType: result.biometryType, // 1=fingerprint, 2=faceId, 3=iris
    };
  } catch (error) {
    console.warn('Biometric check failed:', error);
    return { available: false, biometryType: 'none' };
  }
};

/**
 * Get human-readable biometry type name
 */
export const getBiometryName = (biometryType) => {
  switch (biometryType) {
    case 1: return 'Fingerabdruck';
    case 2: return 'Face ID';
    case 3: return 'Iris';
    case 4: return 'Fingerabdruck / Face ID';
    default: return 'Biometrie';
  }
};

/**
 * Get icon name for the biometry type
 */
export const getBiometryIcon = (biometryType) => {
  switch (biometryType) {
    case 2: return 'face-id';
    default: return 'fingerprint';
  }
};

/**
 * Verify user identity using biometrics
 */
export const authenticateBiometric = async () => {
  if (!isNative()) throw new Error('Biometrics not available on web');

  try {
    await NativeBiometric.verifyIdentity({
      reason: 'Bitte authentifizieren Sie sich, um sich anzumelden',
      title: 'ValuePlus Login',
      subtitle: 'Verwenden Sie Ihre Biometrie zum Anmelden',
      description: 'Legen Sie Ihren Finger auf den Sensor oder schauen Sie auf Ihr Gerät',
    });
    return true;
  } catch (error) {
    console.warn('Biometric authentication failed:', error);
    return false;
  }
};

/**
 * Save credentials securely for biometric login
 */
export const saveCredentials = async (email, password) => {
  if (!isNative()) return false;

  try {
    await NativeBiometric.setCredentials({
      username: email,
      password: password,
      server: 'com.valueplus.investor',
    });
    await Preferences.set({
      key: BIOMETRIC_ENABLED_KEY,
      value: 'true',
    });
    return true;
  } catch (error) {
    console.error('Failed to save biometric credentials:', error);
    return false;
  }
};

/**
 * Retrieve stored credentials after biometric verification
 */
export const getCredentials = async () => {
  if (!isNative()) return null;

  try {
    const credentials = await NativeBiometric.getCredentials({
      server: 'com.valueplus.investor',
    });
    return {
      email: credentials.username,
      password: credentials.password,
    };
  } catch (error) {
    console.warn('Failed to retrieve credentials:', error);
    return null;
  }
};

/**
 * Check if biometric login has been enabled by user
 */
export const isBiometricLoginEnabled = async () => {
  if (!isNative()) return false;

  try {
    const { value } = await Preferences.get({ key: BIOMETRIC_ENABLED_KEY });
    return value === 'true';
  } catch {
    return false;
  }
};

/**
 * Disable biometric login and clear stored credentials
 */
export const disableBiometricLogin = async () => {
  if (!isNative()) return;

  try {
    await NativeBiometric.deleteCredentials({
      server: 'com.valueplus.investor',
    });
    await Preferences.set({
      key: BIOMETRIC_ENABLED_KEY,
      value: 'false',
    });
  } catch (error) {
    console.warn('Failed to disable biometric login:', error);
  }
};

/**
 * Full biometric login flow:
 * 1. Verify identity with biometrics
 * 2. Retrieve stored credentials
 * 3. Return credentials for login
 */
export const performBiometricLogin = async () => {
  const authenticated = await authenticateBiometric();
  if (!authenticated) {
    throw new Error('Biometrische Authentifizierung fehlgeschlagen');
  }

  const credentials = await getCredentials();
  if (!credentials) {
    throw new Error('Keine gespeicherten Anmeldedaten gefunden. Bitte melden Sie sich zuerst manuell an.');
  }

  return credentials;
};
